import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import { useAuth } from '../auth/useAuth'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import SettingsList, { type SettingGroup } from '../components/SettingsList'

/**
 * "Configuración" — parámetros de operación del sistema.
 *
 * Usa el arquetipo `SettingsList` del rediseño. Antes esta página traía su
 * propio `Toggle` inline y mezclaba clases de Tailwind con estilos en línea:
 * el mismo control se veía distinto acá y en WhatsApp.
 *
 * Los cambios NO son optimistas: se editan en un borrador y se guardan con un
 * botón. Estos flags cambian lo que el bot hace con clientes reales —
 * `HUBSPOT_WRITE_MODE=ENABLED` empieza a escribir en el CRM— y un toggle que
 * aplica al toque es demasiado fácil de tocar sin querer.
 */

interface AppConfig {
  HUBSPOT_WRITE_MODE: 'DRY_RUN' | 'ENABLED'
  OUTREACH_MODE: 'MANUAL_ALLOWLIST' | 'DISABLED' | 'AUTO_RULES'
  DRIVE_EXPORT_MODE?: string
  WEBHOOK_ENABLED?: boolean
  // Comportamiento del agente en WhatsApp. El handler los lee de CONFIG#GLOBAL
  // con caché de 60s: el cambio aplica sin redesplegar.
  CUESTIONARIO_WHATSAPP_ENABLED?: boolean
  PEDIR_TELEFONO_ENABLED?: boolean
  VALIDAR_TITULAR_ENABLED?: boolean
}

const HUBSPOT_OPCIONES: Array<[AppConfig['HUBSPOT_WRITE_MODE'], string]> = [
  ['DRY_RUN', 'DRY_RUN — se loguea, no se escribe en HubSpot'],
  ['ENABLED', 'ENABLED — escribe en HubSpot'],
]

const OUTREACH_OPCIONES: Array<[AppConfig['OUTREACH_MODE'], string]> = [
  ['DISABLED', 'DISABLED — no se contacta a nadie'],
  ['MANUAL_ALLOWLIST', 'MANUAL_ALLOWLIST — solo números de la lista permitida'],
  ['AUTO_RULES', 'AUTO_RULES — reglas automáticas'],
]

export default function Config() {
  const { isAdmin } = useAuth()
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [form, setForm] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get<AppConfig>('/config')
      setConfig(response.data)
      setForm(response.data)
    } catch (err) {
      setError(errorMessage(err, 'Error al cargar la configuración'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchConfig() }, [fetchConfig])

  const handleSave = async () => {
    if (!form) return
    setSaving(true)
    setSaveError(null)
    setSaveSuccess(false)
    try {
      await apiClient.put('/config', form)
      setConfig(form)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setSaveError(errorMessage(err, 'Error al guardar la configuración'))
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = form && config ? JSON.stringify(form) !== JSON.stringify(config) : false

  if (loading) return <LoadingSpinner label="Cargando configuración..." />
  if (error) return <ErrorAlert message={error} onRetry={fetchConfig} />
  if (!form) return null

  const set = <K extends keyof AppConfig>(clave: K, valor: AppConfig[K]) =>
    setForm((prev) => (prev ? { ...prev, [clave]: valor } : prev))

  const selector = <T extends string>(
    valor: T, opciones: Array<[T, string]>, onChange: (v: T) => void,
  ) => (
    <select
      value={valor}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={!isAdmin}
      className="input max-w-[380px] disabled:bg-surface-subtle disabled:text-ink-muted"
    >
      {opciones.map(([v, etiqueta]) => (
        <option key={v} value={v}>{etiqueta}</option>
      ))}
    </select>
  )

  // El cuestionario apagado deshabilita los nodos registrales: son un subconjunto
  // de sus preguntas, encenderlos sin él no haría nada.
  const cuestionarioOn = form.CUESTIONARIO_WHATSAPP_ENABLED !== false

  const grupos: SettingGroup[] = [
    {
      title: 'Escritura hacia sistemas externos',
      description: 'Qué puede modificar el bot fuera del portal. En duda, dejar en el modo más restrictivo.',
      rows: [
        {
          key: 'hubspot',
          label: 'HubSpot',
          description: 'En DRY_RUN los cambios se registran en el log pero no llegan al CRM.',
          control: selector(form.HUBSPOT_WRITE_MODE, HUBSPOT_OPCIONES,
            (v) => set('HUBSPOT_WRITE_MODE', v)),
        },
        {
          key: 'outreach',
          label: 'Contacto saliente',
          description: 'Con quién puede iniciar conversación el agente.',
          control: selector(form.OUTREACH_MODE, OUTREACH_OPCIONES,
            (v) => set('OUTREACH_MODE', v)),
        },
        {
          key: 'webhook',
          label: 'Webhook habilitado',
          description: 'Entrega de eventos firmados a los consumidores externos.',
          toggle: {
            checked: Boolean(form.WEBHOOK_ENABLED),
            disabled: !isAdmin,
            onChange: (v) => set('WEBHOOK_ENABLED', v),
          },
        },
      ],
    },
    {
      title: 'Comportamiento del agente en WhatsApp',
      description: 'Se leen de CONFIG#GLOBAL con caché de 60 s: el cambio aplica sin redesplegar.',
      rows: [
        {
          key: 'cuestionario',
          label: 'Cuestionario por WhatsApp',
          description: 'Encendido: el bot hace las 5 preguntas filtro (§9.7–9.11) y '
            + 'arma el checklist según las respuestas. Apagado: entrega el checklist '
            + 'base y pasa directo a recibir documentos. El PDD pide las preguntas, '
            + 'así que apagarlo es salirse del alcance aprobado.',
          toggle: {
            checked: cuestionarioOn,
            disabled: !isAdmin,
            onChange: (v) => set('CUESTIONARIO_WHATSAPP_ENABLED', v),
          },
        },
        {
          key: 'pedir-telefono',
          label: 'Pedir el número en el saludo',
          description: 'Solo aplica a quien llega con alias de WhatsApp: Meta no nos '
            + 'manda su teléfono y sin él un asesor no puede llamarlo ni cruzarlo con '
            + 'su lead. Encendido: el saludo se lo pide. Apagado: no se lo pide, pero '
            + 'si lo escribe o comparte su contacto se registra igual. En producción '
            + 'ese dato llega en el handoff de Software, así que apagarlo sirve para '
            + 'probar la atención sin esa fricción.',
          toggle: {
            checked: form.PEDIR_TELEFONO_ENABLED !== false,
            disabled: !isAdmin,
            onChange: (v) => set('PEDIR_TELEFONO_ENABLED', v),
          },
        },
        {
          key: 'validar-titular',
          label: 'Validar que el DNI sea del solicitante',
          description: 'Regla del PDD (§9.17): el bot compara el titular del '
            + 'documento contra el nombre registrado en la sesión y, si no '
            + 'coincide, pide el DNI correcto y gasta uno de los 3 intentos. '
            + 'En producción va ENCENDIDO: el nombre llega en el handoff de '
            + 'Software y es el control que evita cargar el DNI de otra persona. '
            + 'Apágalo solo en TEST, donde no hay precalificador y los '
            + 'documentos de prueba son de otra persona: el rechazo es correcto '
            + 'pero tapa el flujo que se quiere validar. Apagado NO se pierde el '
            + 'cruce entre documentos: un segundo DNI con otro nombre sigue yendo '
            + 'al requisito del cónyuge y la Copia Literal a nombre de un '
            + 'tercero sigue objetándose.',
          toggle: {
            checked: form.VALIDAR_TITULAR_ENABLED !== false,
            disabled: !isAdmin,
            onChange: (v) => set('VALIDAR_TITULAR_ENABLED', v),
          },
        },
      ],
    },
  ]

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {saveSuccess && (
        <p className="rounded-card border border-brand-border bg-brand-light px-4 py-3 text-body font-semibold text-brand-hover">
          Configuración guardada.
        </p>
      )}
      {saveError && <ErrorAlert message={saveError} />}

      <SettingsList groups={grupos} />

      {isAdmin ? (
        <div className="flex items-center gap-2">
          <button
            onClick={() => void handleSave()}
            disabled={saving || !hasChanges}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
          {hasChanges && (
            <button onClick={() => setForm(config)} className="btn-ghost">Descartar</button>
          )}
          {!hasChanges && (
            <span className="text-micro text-ink-faint">No hay cambios sin guardar.</span>
          )}
        </div>
      ) : (
        <p className="text-micro text-ink-faint">
          Solo un administrador puede modificar la configuración.
        </p>
      )}
    </div>
  )
}
